import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { sessionApi, SessionResponse } from '../api/sessions'
import { useAuth } from './AuthContext'
import { useCorpus } from './CorpusContext'

const ACTIVE_SESSION_KEY = 'pmc_ev_active_session'

interface SessionContextType {
  sessions: SessionResponse[]
  activeSessionId: string
  activeSession: SessionResponse | null
  setActiveSessionId: (id: string) => void
  refreshSessions: () => Promise<void>
  createSession: (name: string) => Promise<SessionResponse>
  deleteSession: (id: string) => Promise<void>
}

const SessionContext = createContext<SessionContextType | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { activeCorpusId } = useCorpus()
  const [sessions, setSessions] = useState<SessionResponse[]>([])
  const [activeSessionId, setActiveSessionIdState] = useState<string>(
    () => localStorage.getItem(ACTIVE_SESSION_KEY) || ''
  )

  const setActiveSessionId = useCallback((id: string) => {
    setActiveSessionIdState(id)
    if (id) localStorage.setItem(ACTIVE_SESSION_KEY, id)
    else localStorage.removeItem(ACTIVE_SESSION_KEY)
  }, [])

  // Sessions belong to a database — only ever show the active corpus's sessions.
  const refreshSessions = useCallback(async () => {
    if (!activeCorpusId) {
      setSessions([])
      return
    }
    try {
      const res = await sessionApi.list(activeCorpusId)
      setSessions(res.data)
    } catch {
      // silent — leave existing list
    }
  }, [activeCorpusId])

  const createSession = useCallback(async (name: string) => {
    if (!activeCorpusId) throw new Error('Select a database before creating a session.')
    const res = await sessionApi.create({ name: name.trim(), corpus_id: activeCorpusId })
    await refreshSessions()
    setActiveSessionId(res.data.session_id)
    return res.data
  }, [activeCorpusId, refreshSessions, setActiveSessionId])

  const deleteSession = useCallback(async (id: string) => {
    await sessionApi.delete(id)
    await refreshSessions()
    // Clear the active selection if we just deleted it.
    setActiveSessionIdState(prev => {
      if (prev === id) {
        localStorage.removeItem(ACTIVE_SESSION_KEY)
        return ''
      }
      return prev
    })
  }, [refreshSessions])

  // Load sessions on login and whenever the active database changes; clear on logout.
  useEffect(() => {
    if (isAuthenticated) {
      refreshSessions()
    } else {
      setSessions([])
      setActiveSessionId('')
    }
  }, [isAuthenticated, refreshSessions, setActiveSessionId])

  // Drop the active session if it isn't among the current database's sessions —
  // e.g. after switching databases, deleting it elsewhere, or losing access.
  // (During a corpus switch `sessions` still holds the previous list until the
  // refresh resolves, so a still-valid id survives the transition.)
  useEffect(() => {
    if (!activeCorpusId) {
      if (activeSessionId) setActiveSessionId('')
      return
    }
    if (activeSessionId && !sessions.some(s => s.session_id === activeSessionId)) {
      setActiveSessionId('')
    }
  }, [sessions, activeSessionId, activeCorpusId, setActiveSessionId])

  const activeSession = sessions.find(s => s.session_id === activeSessionId) || null

  return (
    <SessionContext.Provider
      value={{
        sessions,
        activeSessionId,
        activeSession,
        setActiveSessionId,
        refreshSessions,
        createSession,
        deleteSession,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession must be used within SessionProvider')
  return context
}
